import { LoaderCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { InterfaceStatusType } from '@/types';

import InterfaceServiceRow from '@/components/interface-status/interface-service-row';
import InterfaceStatusBadge from '@/components/interface-status/interface-status-badge';
import StatusMessage from '@/components/shared/status-message';
import { Separator } from '@/components/ui/separator';
import useSocketSubscription from '@/hooks/use-socket-subscription';
import { ERROR_FEEDBACK } from '@/utils/constants';

const InterfaceStatus = () => {
  const { t } = useTranslation();

  const { data, isLoading, error, refetch } =
    useSocketSubscription<InterfaceStatusType>('interface_monitor');

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <LoaderCircle className="size-12 animate-spin text-primary" />
        </div>
      );
    }

    if (!!error && !data) {
      return (
        <div className="flex flex-1 items-center justify-center py-8">
          <StatusMessage
            type="error"
            title={t(ERROR_FEEDBACK.general)}
            description={t(ERROR_FEEDBACK.interface_status.api)}
            showReloadButton
            onRetry={refetch}
            className="items-center text-center"
          />
        </div>
      );
    }

    const { status, coreServices = [], secondaryServices = [] } = data ?? {};

    const serviceGroups = [
      { id: 'core', label: t('interface_status.core_service'), services: coreServices },
      {
        id: 'secondary',
        label: t('interface_status.secondary_services'),
        services: secondaryServices,
      },
    ];

    return (
      <div className="flex flex-col gap-4">
        <InterfaceStatusBadge status={status} />

        <div className="mb-8">
          {serviceGroups.map(({ id, label: groupLabel, services }) => (
            <div key={id} className="flex flex-col gap-3">
              <Separator />
              <div className="max-w-xl">
                <p className="pb-2 text-base font-semibold">{groupLabel}</p>
                {services.map((service) => (
                  <InterfaceServiceRow key={service.serviceName} service={service} id={id} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full gap-8">
      <div className="max-w-xl">
        <h2 className="text-2xl font-semibold text-foreground">{t('interface_status.title')}</h2>
        <p className="text-sm pt-2 text-subtext">{t('interface_status.subtitle')}</p>
      </div>
      {renderContent()}
    </div>
  );
};

export default InterfaceStatus;
